/**
 * Dev tools API — only used when `REACT_APP_DEV_TOOLS_ENABLED` and backend `/dev` are on.
 * All calls use `credentials: 'include'` for the signed `dev_session` cookie.
 */

import { get, post } from '../../shared/apiClient';
import type { RequestConfig } from '../../shared/httpTypes';

const withCreds: RequestConfig = { credentials: 'include' };

export interface DevScreenListItem {
  screen_id: string;
  title: string;
  category: string;
  country: string;
  order: number;
  screen_type: string;
}

export interface DevProfileItem {
  id: string;
  label: string;
  description: string;
  default_country: string;
}

export interface DevJumpRequest {
  target_screen_id: string;
  profile_id: string;
  country?: string;
  tax_year?: number;
  language?: string;
  skip_identification?: boolean;
}

export interface DevJumpResponse {
  session_id: string;
  country: string | null;
  tax_year: number;
  target_screen_id: string;
  applied_answers_count: number;
  auto_filled_keys_count: number;
}

export async function devAuthLogin(token: string): Promise<void> {
  await post<{ token: string }, void>('/dev/auth/login', { token }, withCreds);
}

export async function devAuthWhoami(): Promise<{ authenticated: boolean }> {
  const r = await get<{ authenticated: boolean }>('/dev/auth/whoami', undefined, withCreds);
  return r.data;
}

export async function devFlowScreens(): Promise<DevScreenListItem[]> {
  const r = await get<DevScreenListItem[]>('/dev/flow/screens', undefined, withCreds);
  return r.data;
}

export async function devFlowProfiles(): Promise<DevProfileItem[]> {
  const r = await get<DevProfileItem[]>('/dev/flow/profiles', undefined, withCreds);
  return r.data;
}

export async function devFlowJump(body: DevJumpRequest): Promise<DevJumpResponse> {
  const r = await post<DevJumpRequest, DevJumpResponse>('/dev/flow/jump', body, withCreds);
  return r.data;
}

export async function devAuthLogout(): Promise<void> {
  await post<Record<string, never>, void>('/dev/auth/logout', {}, withCreds);
}

export interface DevSubmitSurveyResponse {
  success: boolean;
  redirect_url?: string | null;
}

export async function devSubmitSurvey(
  sessionId: string,
  surveySessionId?: number,
): Promise<DevSubmitSurveyResponse> {
  const r = await post<{ session_id: string; survey_session_id?: number }, DevSubmitSurveyResponse>(
    '/dev/flow/submit-survey',
    { session_id: sessionId, ...(surveySessionId != null ? { survey_session_id: surveySessionId } : {}) },
    withCreds,
  );
  return r.data;
}
